import React, { useState } from 'react'
import { Box, ButtonBase, Collapse, Stack, Typography } from '@mui/material'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import type { ChartCommandApplyEntry, ChartCommandApplyResults } from '../../screener/aiChat/types'

type Props = {
    results: ChartCommandApplyResults
}

/**
 * Above this many successful changes the list collapses behind a summary row.
 * A long ladder of tick rows buries the assistant's actual reply, and users
 * only scan it to confirm "did it do the thing".
 */
const INLINE_LIMIT = 2

function ResultRow({ entry, tone }: { entry: ChartCommandApplyEntry; tone: 'success' | 'warning' }) {
    const Icon = tone === 'success' ? CheckRoundedIcon : ErrorOutlineRoundedIcon
    return (
        <Stack direction="row" spacing={0.75} alignItems="flex-start">
            <Icon
                sx={{
                    fontSize: 13,
                    mt: '3px',
                    flexShrink: 0,
                    color: tone === 'success' ? 'success.main' : 'warning.main',
                }}
            />
            <Typography
                variant="caption"
                sx={{
                    lineHeight: 1.5,
                    color: tone === 'success' ? 'text.secondary' : 'warning.main',
                    wordBreak: 'break-word',
                }}
            >
                {entry.message}
            </Typography>
        </Stack>
    )
}

export default function ChartCommandResults({ results }: Props) {
    const { applied, failed } = results
    const [expanded, setExpanded] = useState(false)

    if (!applied.length && !failed.length) return null

    const collapsible = applied.length > INLINE_LIMIT

    const summary = [
        applied.length ? `${applied.length} ${applied.length === 1 ? 'change' : 'changes'} applied` : null,
        failed.length ? `${failed.length} failed` : null,
    ]
        .filter(Boolean)
        .join(' · ')

    return (
        <Box
            sx={{
                mt: 1.25,
                px: 1.25,
                py: 0.875,
                borderRadius: 1.5,
                bgcolor: 'action.hover',
                border: 1,
                borderColor: 'divider',
            }}
        >
            {collapsible ? (
                <>
                    <ButtonBase
                        onClick={() => setExpanded((prev) => !prev)}
                        aria-expanded={expanded}
                        sx={{
                            width: '100%',
                            justifyContent: 'flex-start',
                            borderRadius: 1,
                            py: 0.125,
                        }}
                    >
                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ width: '100%', minWidth: 0 }}>
                            <CheckRoundedIcon sx={{ fontSize: 13, color: 'success.main', flexShrink: 0 }} />
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1, textAlign: 'left' }}>
                                {summary}
                            </Typography>
                            <ExpandMoreRoundedIcon
                                sx={{
                                    fontSize: 16,
                                    color: 'text.tertiary',
                                    flexShrink: 0,
                                    transform: expanded ? 'rotate(180deg)' : 'none',
                                    transition: 'transform 150ms ease',
                                }}
                            />
                        </Stack>
                    </ButtonBase>
                    <Collapse in={expanded} unmountOnExit>
                        <Stack spacing={0.5} sx={{ mt: 0.875 }}>
                            {applied.map((item, index) => (
                                <ResultRow key={`applied-${item.op}-${index}`} entry={item} tone="success" />
                            ))}
                        </Stack>
                    </Collapse>
                </>
            ) : (
                <Stack spacing={0.5}>
                    {applied.map((item, index) => (
                        <ResultRow key={`applied-${item.op}-${index}`} entry={item} tone="success" />
                    ))}
                </Stack>
            )}

            {/* Failures always stay visible — they need the user to act. */}
            {failed.length > 0 && (
                <Stack spacing={0.5} sx={{ mt: applied.length ? 0.75 : 0 }}>
                    {failed.map((item, index) => (
                        <ResultRow key={`failed-${item.op}-${index}`} entry={item} tone="warning" />
                    ))}
                </Stack>
            )}
        </Box>
    )
}
